import './styles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Col, Row, Skeleton as AntDSkeleton } from 'antd'
import clsx from 'clsx'
import Skeleton from 'react-loading-skeleton'

import B2BecImageWithFallback from '@/components/B2BecImageWithFallback'
import B2BecLink from '@/components/B2BLink'
import CustomButton from '@/components/CustomButton'
import { PlusIcon } from '@/components/Icons'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectIsLoadingSubCategoryOverview,
  selectSubCategoryOverviewData,
} from '@/store/selectors/categorySelector'
import { trackCategory } from '@/store/slices/adobeAnalyticSlice'
import {
  getProductSubCategoryOverviewData,
  resetProductSubCategoryOverviewData,
} from '@/store/slices/categorySlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useCalculateScrollbarWidth from '@/hooks/useCalculateScrollbarWidth'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { isValidArray } from '@/libs/utils/array'

import SubItemList from '../components/SubItemList'
import { SUB_ITEM_LIST_TYPES } from '../product-navigation.constants'

const chunkInto2Array = (array, length, minSize = 5) => {
  const maxSize = Math.ceil(array.length / length)
  const size = Math.max(maxSize, minSize)

  if (!Array.isArray(array) || array.length === 0) {
    return []
  }

  return Array.from({ length }, (_, index) => array.slice(index * size, index * size + size))
}

function SubCategoryOverviewPage(props) {
  const { t } = useTranslation()
  const { categoryId } = useParams()
  const location = useLocation()
  const productTypeLinkPrefix = useMemo(() => {
    const result = location.pathname.match(/^\/accessories|\/detergents/)
    return result && result.length > 0 ? result[0] : ''
  }, [location.pathname])

  const [shouldRenderImageWrapper, setShouldRenderImageWrapper] = useState(true)
  const { title: pageTitle, breadcrumbs, pageId } = props
  const { setTitle, setIsTitleTranslated } = useDocumentTitle(pageTitle)
  const breadcrumbsData = () => {
    switch (productTypeLinkPrefix) {
      case '/accessories': {
        return [
          {
            titleData: [t('breadcrumbTitle.accessories')],
            pathData: ['accessories'],
          },
        ]
      }
      case '/detergents': {
        return [
          {
            titleData: [t('breadcrumbTitle.detergents')],
            pathData: ['detergents'],
          },
        ]
      }
      default: {
        return []
      }
    }
  }
  const [setBreadcrumbsData] = useBreadcrumbs(breadcrumbs, breadcrumbsData)
  const { setPageInfoData } = useAdobeAnalysis(TRACK_DATA.CATEGORIES)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isLoadingSubCategoryOverview = useAppSelector(
    selectIsLoadingSubCategoryOverview,
  )
  const subCategoryOverviewData = useAppSelector(selectSubCategoryOverviewData)

  const {
    categoryName: title = '',
    text: subtitle = '',
    subCategories = [],
  } = subCategoryOverviewData

  useCalculateScrollbarWidth()

  useEffect(
    () => () => {
      dispatch(resetProductSubCategoryOverviewData())
    },
    [dispatch],
  )

  useEffect(() => {
    dispatch(getProductSubCategoryOverviewData({ categoryId }))
  }, [dispatch, categoryId])

  useEffect(() => {
    if (subCategories.length === 0) {
      navigate(`${location.pathname}/subcategory/${categoryId}`, {
        replace: true,
      })
    }
  }, [subCategories, location.pathname, categoryId, navigate])

  useEffect(() => {
    if (title) {
      setPageInfoData({
        pageName: `sub categories overview:${title}`,
        pageType: PAGE_TYPES.SUB_CATEGORIES,
        pageId,
      })
    }
  }, [setPageInfoData, title, pageId])

  useEffect(() => {
    if (title) {
      switch (productTypeLinkPrefix) {
        case '/accessories': {
          setBreadcrumbsData([
            {
              titleData: [t('breadcrumbTitle.accessories')],
              pathData: ['accessories'],
            },
            {
              titleData: [title],
              pathData: ['accessories', categoryId],
            },
          ])
          break
        }
        case '/detergents': {
          setBreadcrumbsData([
            {
              titleData: [t('breadcrumbTitle.detergents')],
              pathData: ['detergents'],
            },
            {
              titleData: [title],
              pathData: ['detergents', categoryId],
            },
          ])
          break
        }
        default: {
          setBreadcrumbsData([
            {
              titleData: [title],
              pathData: [categoryId],
            },
          ])
        }
      }
      setIsTitleTranslated(true)
      setTitle(title)

      dispatch(trackCategory({ primaryCategory: title }))
    }
  }, [
    t,
    setTitle,
    setIsTitleTranslated,
    productTypeLinkPrefix,
    setBreadcrumbsData,
    title,
    categoryId,
    dispatch,
  ])

  const renderButton = useCallback(
    (subCategoryId, options = { isEmptyList: false }) => (
      <div
        className={clsx('sub-item__list-button', {
          'mt-0': options.isEmptyList,
        })}
      >
        <CustomButton
          buttonSize="small"
          icon={PlusIcon}
          type="ghost"
          onClick={() => navigate(`${location.pathname}/subcategory/${subCategoryId}`)}
        >
          {t('productSubCategoryOverview.button.seeMore')}
        </CustomButton>
      </div>
    ),
    [location.pathname, t, navigate],
  )

  const renderClassTypeList = (subCategory) => {
    const { categoryId: subCategoryId, subCategories: classTypeList = [] } = subCategory

    const listDatas = chunkInto2Array(classTypeList, 2)

    if (listDatas.length === 0) {
      return (
        <Col lg={12} md={12} xl={12}>
          {renderButton(subCategoryId, { isEmptyList: true })}
        </Col>
      )
    }

    return listDatas.map((data, index) => {
      const key = `sub-item-${subCategoryId}-${index}`
      const shouldShowSeeMore = index === listDatas.length - 1
      const isEmptyList = data.length === 0

      return (
        <Col key={key} lg={12} md={12} xl={12}>
          <div className="sub-item__list-wrapper">
            <SubItemList
              listData={data}
              mainCategoryId={categoryId}
              subCategoryId={subCategoryId}
              type={SUB_ITEM_LIST_TYPES.SUB_CATEGORY_OVERVIEW}
            />
            {shouldShowSeeMore
              ? renderButton(subCategoryId, { isEmptyList })
              : null}
          </div>
        </Col>
      )
    })
  }

  useCalculateScrollbarWidth()

  const handleSubcategoryImageLoadFailed = (hasLoadedFailed) => {
    if (hasLoadedFailed) {
      setShouldRenderImageWrapper(false)
    } else {
      setShouldRenderImageWrapper(true)
    }
  }

  const renderSubCategoryList = () => {
    return isValidArray(subCategories)
      ? subCategories.map((subCategory) => {
        const imgSource = isValidArray(subCategory.images)
          ? subCategory.images[0]?.url
          : ''
        const shouldShowImage = shouldRenderImageWrapper && !!imgSource
        return (
          <div
            key={subCategory.categoryName}
            className="sub-category-overview__sub-category-item__wrapper"
          >
            <B2BecLink
              className="fadeIn-05"
              to={`${location.pathname}/subcategory/${subCategory.categoryId}`}
            >
              <h2>{subCategory.categoryName}</h2>
            </B2BecLink>
            <Row gutter={[32, 0]}>
              {shouldShowImage
                ? (
                  <Col lg={12} md={12} sm={24} xl={12} xs={24} xxl={10}>
                    <B2BecLink
                      className="fadeIn-2"
                      to={`${location.pathname}/subcategory/${subCategory.categoryId}`}
                    >
                      <B2BecImageWithFallback
                        isHiddenOnError
                        alt={subCategory.categoryName}
                        src={imgSource}
                        onSubcategoryImageLoadFailed={handleSubcategoryImageLoadFailed}
                      />
                    </B2BecLink>
                  </Col>
                )
                : null}
              <Col lg={12} md={12} sm={24} xl={12} xs={24}>
                <p className="sub-category-overview__sub-category-item__description fadeIn-07 mb-3">
                  {subCategory.text}
                </p>
                <Row gutter={24}>{renderClassTypeList(subCategory)}</Row>
              </Col>
            </Row>
          </div>
        )
      })
      : null
  }

  const renderSubCategoryListSkeleton = () => {
    return (
      <div className="sub-category-overview__sub-category-item__wrapper">
        <Row gutter={[32, 0]}>
          <Col lg={12} md={12} sm={24} xl={12} xs={24}>
            <h2>
              <Skeleton />
            </h2>
          </Col>
        </Row>
        <Row gutter={[32, 0]}>
          <Col lg={12} md={12} sm={24} xl={12} xs={24}>
            <Skeleton height={200} width="100%" />
          </Col>
          <Col lg={12} md={12} sm={24} xl={12} xs={24}>
            <p className="sub-category-overview__sub-category-item__description">
              <Skeleton count={3} />
            </p>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <div className="sub-category-overview__wrapper">
      <div className="full-width-page-component__wrapper">
        <div className="sub-category-overview__heading">
          {isLoadingSubCategoryOverview
            ? (
              <div className="sub-category-overview__heading__inner">
                <AntDSkeleton active />
              </div>
            )
            : (
              <div className="sub-category-overview__heading__inner fadeIn-06">
                <h1>{title}</h1>
                <p className="sub-category-overview__heading__subtitle">
                  {subtitle}
                </p>
              </div>
            )}
        </div>
      </div>
      {isLoadingSubCategoryOverview
        ? renderSubCategoryListSkeleton()
        : renderSubCategoryList()}
    </div>
  )
}

export default SubCategoryOverviewPage
